'use strict';

angular.module('inara').factory('DeviceService', DeviceService);

require('./DeviceWifi');
require('./GenericDeviceService');
require('./KayleeService');
require('./KnoxService');
require('./MimoService');
var defaults = require('../../defaults/Defaults');
var Log = require('../../log/Log');
var log = Log.channel('DeviceService');
require('./SharedPrefs');
var semver = require('semver');

/* global AveoAutostart */
/* global AveoDevice */
/* global AveoLog */
/* global AveoPackageReplaced */
/* global AveoPermissions */
/* global AveoRestart */
/* global AveoWatchdog */
/* global AveoShutdown */
/* global FileTransfer */

function inject($injector, name, envs) {
    return envs.includes(process.env.appEnv) ? $injector.get(name) : null;
}

DeviceService.$inject = [
    '$injector',
    '$q',
    '$timeout',
    'KayleeService',
    'SharedPrefs',
    '$rootScope',
];

function DeviceService($injector, $q, $timeout, KayleeService, SharedPrefs, $rootScope) {
    var DeviceWifi = inject($injector, 'DeviceWifi', ['cordova']);
    var ManagerService = null;
    var deviceAdminEnabled = false;
    var platformInfo = null;
    var networkConnection = 'none';
    var loadedPromises = [];
    var ready = false;
    var readyListeners = [];
    var defaultWallpaperPath;
    var rgbLedValue = { red: null, green: null, blue: null };
    var pendingRgbLedValue = null;
    var settingRgbLedValue = false;

    init();

    return {
        acquireFullWakeLock: acquireFullWakeLock,
        addReadyListener: addReadyListener,
        causeAnr: causeAnr,
        cleanUpFiles: cleanUpFiles,
        enableDeviceAdminForComponent: enableDeviceAdminForComponent,
        finishUpdates: finishUpdates,
        forgetWifiNetwork: forgetWifiNetwork,
        getCaps: function () {
            return ManagerService ? ManagerService.caps : null;
        },
        getCurrentWifiNetwork: getCurrentWifiNetwork,
        getDeviceAdminActiveForComponent: getDeviceAdminActiveForComponent,
        getDeviceManagerName: function () {
            return ManagerService ? ManagerService.name : null;
        },
        getFactoryInstallChecklist: getFactoryInstallChecklist,
        getNetworkConnection: getNetworkConnection,
        getNetworkInterfaces: getNetworkInterfaces,
        getPlatformInfo: getPlatformInfo,
        getSerialNumber: getSerialNumber,
        getWifiNetworks: getWifiNetworks,
        isWifiConnected: isWifiConnected,
        isWifiEnabled: isWifiEnabled,
        joinWifiNetwork: joinWifiNetwork,
        loaded: loaded,
        needsSystemUpdate: needsSystemUpdate,
        restartDevice: restartDevice,
        restartInara: restartInara,
        setAllowOtaUpgrade: setAllowOtaUpgrade,
        setEthernetState: setEthernetState,
        setKioskMode: setKioskMode,
        setRgbLeds: setRgbLeds,
        setSleep: setSleep,
        setWake: setWake,
        setWallpaper: setWallpaper,
        setWifiEnabled: setWifiEnabled,
        throwException: throwException,
        uninstallApplication: uninstallApplication,
        updateApplication: updateApplication,
        updateKnoxAnimations: updateKnoxAnimations,
        updateSystem: updateSystem,
    };

    function init() {
        log.info('initializing');
        log.info('window.navigator.userAgent = ' + window.navigator.userAgent);

        if (document.readyState === 'complete') {
            onDocumentLoaded();
        } else {
            window.addEventListener('load', onDocumentLoaded);
        }
    }

    function acquireFullWakeLock() {
        return AveoDevice.acquireFullWakeLock();
    }

    function addReadyListener(listener) {
        if (ready) {
            log.debug('already ready, calling listener now');
            $timeout(function () {
                listener();
            }, 1);
        } else {
            log.debug('not ready yet, adding listener to readyListeners');
            readyListeners.push(listener);
        }
    }

    function causeAnr() {
        return AveoDevice.causeAnr();
    }

    function cleanUpFiles() {
        if (!ManagerService.hasOwnProperty('cleanUpFiles')) {
            return $q.resolve();
        }

        return ManagerService.cleanUpFiles();
    }

    function enableAutostart() {
        if (ManagerService.name === 'generic') return $q.resolve();

        if (!ManagerService.hasOwnProperty('enableAutostart')) {
            return AveoAutostart.enableAutostart(true);
        }

        return ManagerService.enableAutostart();
    }

    function enableDeviceAdmin() {
        if (deviceAdminEnabled) {
            log.info('device admin already enabled');
            return $q.resolve();
        }

        log.info('enabling device admin');

        return AveoDevice.enableDeviceAdmin()
            .then(function (message) {
                log.info('device admin enabled: ' + message);
                deviceAdminEnabled = true;
            })
            .catch(function (err) {
                log.error('failed enabling device admin, error: ' + err);
                deviceAdminEnabled = false;
            })
            .then(function () {
                return SharedPrefs.setBoolean('deviceAdminEnabled', deviceAdminEnabled);
            })
            .catch(function (err) {
                log.error('failed setting deviceAdminEnabled in sharedPrefs, error: ' + err);
            })
            .then(function () {
                if (!deviceAdminEnabled) {
                    throw new Error('failed enabling device admin');
                }
            });
    }

    function enableDeviceAdminForComponent(packageName, className) {
        log.info('enabling device admin for ' + packageName + '.' + className);

        return AveoDevice.enableDeviceAdminForComponent(packageName, className)
            .then(function (message) {
                log.info(
                    'device admin enabled for ' + packageName + ', ' + className + ': ' + message
                );
            })
            .catch(function (err) {
                log.error(
                    'failed enabling device admin for ' +
                        packageName +
                        ', ' +
                        className +
                        ', error: ' +
                        err
                );
                return $q.reject(err);
            });
    }

    function finishUpdates(updates) {
        if (!ManagerService.hasOwnProperty('finishUpdates')) {
            return $q.resolve();
        }

        return ManagerService.finishUpdates(updates);
    }

    function forgetWifiNetwork(ssid) {
        if (process.env.appEnv === 'browser') return;

        return DeviceWifi.removeNetwork(ssid);
    }

    function getCurrentWifiNetwork() {
        if (process.env.appEnv === 'browser') return $q.resolve('');

        return $q(function (resolve, reject) {
            DeviceWifi.getCurrentSSID().then(
                function (ssid) {
                    resolve(ssid.replace(/^"|"$/g, ''));
                },
                function (reason) {
                    reject(reason);
                }
            );
        });
    }

    function getDeviceAdminActiveForComponent(packageName, className) {
        return AveoDevice.getDeviceAdminActiveForComponent(packageName, className);
    }

    function getFactoryInstallChecklist() {
        if (!ManagerService.hasOwnProperty('getFactoryInstallChecklist')) {
            return [];
        }

        return ManagerService.getFactoryInstallChecklist();
    }

    function getNetworkConnection() {
        if (process.env.appEnv === 'browser') return 'unknown';

        // supplied by the Connection plugin
        return navigator.connection ? navigator.connection.type : 'unknown';
    }

    function getNetworkInterfaces() {
        return AveoDevice.getNetworkInterfaces();
    }

    function getPlatformInfo() {
        return $q.resolve(platformInfo);
    }

    function getSerialNumber() {
        if (!ManagerService.hasOwnProperty('getSerialNumber')) {
            log.error('getSerialNumber not supported on this platform');
            return 'unknown';
        }

        return ManagerService.getSerialNumber();
    }

    function getWifiNetworks() {
        if (process.env.appEnv === 'browser') return [];

        // getScanResults returns networks sorted by signal strength
        // We also remove duplicate SSIDs
        return DeviceWifi.isWifiEnabled()
            .then(DeviceWifi.startScan())
            .then(function () {
                return DeviceWifi.getScanResults(null);
            })
            .then(function (networks) {
                return uniqWifiNetworks(networks);
            });
    }

    function isWifiConnected(timeout) {
        if (process.env.appEnv === 'browser') return false;

        var checkTimeMs = 500;
        // If timeout in seconds is not provided, set it to 60 seconds
        if (!angular.isDefined(timeout)) {
            timeout = Math.floor((1000 / checkTimeMs) * 60);
        } else {
            timeout = Math.floor((1000 / checkTimeMs) * timeout);
        }

        return $q(function (resolve, reject) {
            var i = 0;
            $timeout(function () {
                checkWifiConnection();
            }, checkTimeMs);

            function checkWifiConnection() {
                AveoDevice.getWifiSupplicantState().then(function (state) {
                    log.info('wifi supplicant state = ' + state);
                    if (state === 'COMPLETED') {
                        resolve(true);
                    } else if (
                        state === 'INACTIVE' ||
                        state === 'INTERFACE_DISABLED' ||
                        state === 'INVALID' ||
                        state === 'UNINITIALIZED'
                    ) {
                        resolve(false);
                    } else if (i < timeout) {
                        i++;
                        $timeout(function () {
                            checkWifiConnection();
                        }, checkTimeMs);
                    } else {
                        reject('Timed out');
                    }
                });
            }
        });
    }

    function isWifiEnabled() {
        return DeviceWifi.isWifiEnabled();
    }

    function joinWifiNetwork(network, username, password) {
        if (process.env.appEnv === 'browser') return;

        return DeviceWifi.addNetwork(network, username, password).then(function () {
            return DeviceWifi.connectNetwork(network.SSID);
        });
    }

    function loaded() {
        if (ready) return $q.resolve();

        var deferredPromise = $q.defer();
        loadedPromises.push(deferredPromise);
        return deferredPromise.promise;
    }

    function notifyReadyListeners() {
        log.debug('notifyReadyListeners');

        ready = true;

        for (var i = 0; i < readyListeners.length; i++) {
            readyListeners[i]();
        }

        readyListeners.length = 0;
    }

    function onDeviceReady() {
        navigator.splashscreen.hide();

        SharedPrefs.getBoolean('useSystemLogs', false).then(function (useSystemLogs) {
            if (useSystemLogs) {
                log.info('switching to Android logs');

                Log.logger = {
                    debug: AveoLog.debug,
                    error: AveoLog.error,
                    info: AveoLog.info,
                    warning: AveoLog.warning,
                };
            }
        });

        defaultWallpaperPath =
            process.env.appEnv === 'browser'
                ? ''
                : cordova.file.applicationDirectory + defaults.inara.platformWallpaper;

        AveoWatchdog.getStartInfo().then(function (info) {
            log.info('start info = ' + JSON.stringify(info, null, 4));
        });

        log.info('enabling watchdog');
        AveoWatchdog.setWatchdogEnabled(true);

        AveoShutdown.onShutdown(onShutdown);

        // set package replacement tracking to default (own package only)
        AveoPackageReplaced.registerPackages();

        networkConnection = getNetworkConnection();
        document.addEventListener('online', onNetworkConnectionOnline);
        document.addEventListener('offline', onNetworkConnectionOffline);

        $q.all([SharedPrefs.getBoolean('deviceAdminEnabled', false)])
            .then(function (data) {
                deviceAdminEnabled = data[0];

                log.debug('deviceAdminEnabled = ' + deviceAdminEnabled);
            })
            .then(function () {
                return cordova.getAppVersion.getVersionNumber().then(function (version) {
                    log.info('Inara version ' + version);
                });
            })
            .then(KayleeService.isInitialized)
            .then(function () {
                return $q(function (resolve, reject) {
                    var connected = null;
                    var isWorkingOnline = null;
                    var connectedListener = null;
                    var isWorkingOnlineListener = null;

                    function checkConnection() {
                        if (connected === null || isWorkingOnline === null) return;

                        if ((connected && isWorkingOnline) || (!connected && !isWorkingOnline)) {
                            KayleeService.offConnectionStateChanged(connectedListener);
                            KayleeService.offWorkingOnlineChanged(isWorkingOnlineListener);
                            resolve();
                        }
                    }

                    connectedListener = KayleeService.onConnectionStateChanged(function (
                        newConnected
                    ) {
                        connected = newConnected;
                        checkConnection();
                    });

                    isWorkingOnlineListener = KayleeService.onWorkingOnlineChanged(function (
                        newIsWorkingOnline
                    ) {
                        isWorkingOnline = newIsWorkingOnline;
                        checkConnection();
                    });
                });
            })
            .then(AveoDevice.getPlatformInfo)
            .then(function (pinfo) {
                platformInfo = pinfo;
                log.debug('platformInfo = ' + JSON.stringify(platformInfo, null, 4));

                if (platformInfo.manufacturer === 'samsung' && device.model === 'SM-T560NU') {
                    ManagerService = inject($injector, 'KnoxService', ['cordova']);
                } else if (
                    platformInfo.manufacturer === 'ZDMID' &&
                    ['Aveo', 'AVEO', 'Mimo', 'MIMO'].includes(platformInfo.brand)
                ) {
                    ManagerService = inject($injector, 'MimoService', ['cordova']);
                } else {
                    ManagerService = inject($injector, 'GenericDeviceService', ['cordova']);
                }

                log.info(ManagerService.name + ' platform detected');
            })
            .then(function () {
                if (ManagerService.name !== 'generic') return enableDeviceAdmin();
            })
            .then(function () {
                return ManagerService.initialize(platformInfo);
            })
            .then(function () {
                if (
                    !ManagerService.hasOwnProperty('requiredPermissions') ||
                    ManagerService.requiredPermissions.length === 0
                ) {
                    return $q.resolve();
                }

                log.info(
                    'checking permissions: ' + JSON.stringify(ManagerService.requiredPermissions)
                );

                return AveoPermissions.checkPermissions(ManagerService.requiredPermissions)
                    .then(function (hasPermission) {
                        var neededPermissions = [];

                        ManagerService.requiredPermissions.forEach(function (permission, i) {
                            if (hasPermission[i]) {
                                log.info(
                                    ManagerService.requiredPermissions[i] +
                                        ' permission already granted'
                                );
                            } else {
                                log.info(
                                    ManagerService.requiredPermissions[i] +
                                        ' permission not yet granted'
                                );
                                neededPermissions.push(ManagerService.requiredPermissions[i]);
                            }
                        });

                        if (neededPermissions.length === 0) return $q.resolve();

                        log.info('requesting permissions: ' + JSON.stringify(neededPermissions));

                        return AveoPermissions.requestPermissions(neededPermissions)
                            .then(function (hasPermissions) {
                                if (hasPermissions) {
                                    log.info('permissions granted');
                                } else {
                                    log.error('permissions denied');
                                }
                            })
                            .catch(function (err) {
                                log.error('error requesting permissions, error = ' + err);
                                return $q.resolve();
                            });
                    })
                    .catch(function (err) {
                        log.error('error checking permissions, error = ' + err);
                        return $q.resolve();
                    });
            })
            .then(enableAutostart)
            .then(setWallpaper)
            .then(notifyReadyListeners)
            .then(resolveLoadedPromises)
            .catch(function (err) {
                log.error('failed initializing, error: ' + err);
                return $q.reject(err);
            });
    }

    function onDocumentLoaded() {
        if (process.env.appEnv === 'cordova') {
            window.screen.orientation.lock('landscape-primary');
        }

        document.addEventListener('deviceready', onDeviceReady);
    }

    function onNetworkConnectionOnline() {
        var newConnection = getNetworkConnection();
        if (networkConnection !== newConnection) {
            networkConnection = newConnection;
            log.debug('Network connection online, using: ' + networkConnection);

            if (
                networkConnection === 'wifi' &&
                device.platform === 'Android' &&
                semver.satisfies(device.version, '6.x')
            ) {
                // Android 6 hack: If we just changed to wifi, toggle it off
                // then on to ensure that Firebase realizes the new network
                // connection. Have you tried turning it off and on again?
                log.debug('toggling wifi state for Android 6');
                setWifiEnabled(false).finally(function () {
                    setWifiEnabled(true);
                });
            }
        }
    }

    function onNetworkConnectionOffline() {
        log.debug('Network connection offline');
    }

    function onShutdown() {
        log.debug('Device is shutting down');
        $rootScope.$broadcast('DeviceShutdown');
        // Let Kaylee know we are disconnecting so we don't have to wait for
        // the database connection state timeout.
        if (KayleeService.isInitialized()) KayleeService.setConnected(false);
    }

    function restartDevice(delayMs) {
        if (typeof ManagerService.restartDevice !== 'function') {
            log.error('restartDevice not supported on this platform');
            return;
        }

        if (typeof delayMs === 'undefined') delayMs = 1000;
        ManagerService.restartDevice(delayMs);
    }

    function restartInara(delayMs) {
        if (typeof delayMs === 'undefined') delayMs = 1000;
        $timeout(function () {
            AveoRestart.restartApplication(500);
        }, delayMs);
    }

    function resolveLoadedPromises() {
        log.debug('resolveLoadedPromises');

        loadedPromises.forEach(function (promise) {
            promise.resolve();
        });
    }

    function setAllowOtaUpgrade(enabled) {
        if (!ManagerService.hasOwnProperty('setAllowOtaUpgrade')) {
            log.error('setAllowOtaUpgrade not supported on this platform');
            return $q.resolve();
        }

        return ManagerService.setAllowOtaUpgrade(enabled);
    }

    function setEthernetState(enabled) {
        if (!ManagerService.hasOwnProperty('setEthernetState')) {
            log.info('setEthernetState not supported on this platform');
            return $q.resolve();
        }

        return ManagerService.setEthernetState(enabled);
    }

    function setKioskMode(enabled) {
        if (!ManagerService.hasOwnProperty('setKioskMode')) {
            log.warning('setKioskMode not supported on this platform');
            return $q.resolve();
        }

        return ManagerService.setKioskMode(enabled);
    }

    function setRgbLeds(red, green, blue) {
        if (!ManagerService.hasOwnProperty('setRgbLeds')) return;

        if (settingRgbLedValue) {
            pendingRgbLedValue = { red: red, green: green, blue: blue };
            return;
        }

        function internalSetRgbLeds(red, green, blue) {
            settingRgbLedValue = true;

            if (
                rgbLedValue.red === red &&
                rgbLedValue.green === green &&
                rgbLedValue.blue === blue
            ) {
                settingRgbLedValue = false;
                return;
            }

            ManagerService.setRgbLeds(red, green, blue)
                .then(function () {
                    rgbLedValue = { red: red, green: green, blue: blue };

                    if (pendingRgbLedValue) {
                        var tmpValue = pendingRgbLedValue;
                        pendingRgbLedValue = null;
                        internalSetRgbLeds(tmpValue.red, tmpValue.green, tmpValue.blue);
                    } else {
                        settingRgbLedValue = false;
                    }
                })
                .catch(function () {
                    log.warning('failed setting leds, retrying');

                    if (pendingRgbLedValue) {
                        var tmpValue = pendingRgbLedValue;
                        pendingRgbLedValue = null;
                        internalSetRgbLeds(tmpValue.red, tmpValue.green, tmpValue.blue);
                    } else {
                        internalSetRgbLeds(red, green, blue);
                    }
                });
        }

        internalSetRgbLeds(red, green, blue);
        return;
    }

    function setSleep() {
        if (!ManagerService.hasOwnProperty('setSleep')) {
            return AveoDevice.setScreenDim(true);
        }

        return ManagerService.setSleep();
    }

    function setWake() {
        if (!ManagerService.hasOwnProperty('setWake')) {
            return AveoDevice.setScreenDim(false);
        }

        return ManagerService.setWake();
    }

    function setWallpaper() {
        if (ManagerService.name === 'generic') return $q.resolve();

        // Set the device wallpaper (the image visible behind the launcher)
        log.info('setting device wallpaper from: ' + defaultWallpaperPath);
        return $q(function (resolve) {
            window.resolveLocalFileSystemURL(defaultWallpaperPath, resolve);
        })
            .then(function (entry) {
                var fileTransfer = new FileTransfer();
                var targetFile = cordova.file.dataDirectory + 'device-wallpaper';
                return $q(function (resolve, reject) {
                    fileTransfer.download(entry.toURL(), targetFile, resolve, reject);
                }).then(function () {
                    return $q.resolve(targetFile.replace(/^file:\/\//, ''));
                });
            })
            .then(function (targetFile) {
                if (!ManagerService.hasOwnProperty('setWallpaper')) {
                    return AveoDevice.setWallpaper(targetFile);
                }

                return ManagerService.setWallpaper(targetFile);
            })
            .catch(function (err) {
                log.error(err);
            });
    }

    function setWifiEnabled(enable) {
        return DeviceWifi.setWifiEnabled(enable);
    }

    function throwException(thread) {
        AveoDevice.throwException(thread);
    }

    function uniqWifiNetworks(networks) {
        // Remove duplicate SSIDs
        return $q(function (resolve, reject) {
            var exists = {};
            var uniq = networks.filter(function (item, pos, array) {
                if (!exists[item.SSID]) {
                    exists[item.SSID] = true;
                    return true;
                }
                return false;
            });
            resolve(uniq);
        });
    }

    function uninstallApplication(update) {
        if (!ManagerService.hasOwnProperty('uninstallApplication')) {
            log.error('uninstallApplication not supported on this platform');
            return $q.resolve();
        }

        return ManagerService.uninstallApplication(update);
    }

    function updateApplication(update, onProgress) {
        if (!ManagerService.hasOwnProperty('updateApplication')) {
            log.error('updateApplication not supported on this platform');
            return $q.resolve();
        }

        return ManagerService.updateApplication(update, onProgress);
    }

    function updateKnoxAnimations(update, onProgress) {
        if (!ManagerService.hasOwnProperty('updateKnoxAnimations')) {
            log.error('updateKnoxAnimations not supported on this platform');
            return $q.resolve();
        }

        return ManagerService.updateKnoxAnimations(update, onProgress);
    }

    function needsSystemUpdate(update) {
        if (!ManagerService.hasOwnProperty('needsSystemUpdate')) {
            log.error('needsSystemUpdate not supported on this platform');
            return false;
        }

        return ManagerService.needsSystemUpdate(update);
    }

    function updateSystem(update, onProgress) {
        if (!ManagerService.hasOwnProperty('updateSystem')) {
            log.error('updateSystem not supported on this platform');
            return $q.resolve();
        }

        return ManagerService.updateSystem(update, onProgress);
    }
}

module.exports = DeviceService;
